import { useMemo, useState } from "react";
import { Alert } from "reactstrap";
import AvailabilityPopover from "../AvailabilityPopover";
import { availabilityDetail, daysWindow, nextMonthWindow, summarizeUnits } from "../unitStats";
import "../portfolioTable.css";

/** The windows a manager can pull the report for; 90 days opens by default (owner, 2026-10-02). */
const WINDOWS = [
  { id: "nextMonth", label: (w) => `To ${w.endLabel} (end of next month)`, make: () => nextMonthWindow() },
  { id: "30", label: () => "Next 30 days", make: () => daysWindow(30) },
  { id: "60", label: () => "Next 60 days", make: () => daysWindow(60) },
  { id: "90", label: () => "Next 90 days", make: () => daysWindow(90) },
];

/** The same three views as the Mastersheet tiles' popovers. */
const VIEWS = [
  { id: "ending", label: "Leases ending" },
  { id: "rented", label: "Renewed / Rented / Pending" },
  { id: "vacant", label: "Vacant now" },
];

const Kpi = ({ label, value, sub, tone = "" }) => (
  <div className={`pft-stat ${tone}`.trim()}>
    <div className="pft-stat__label">{label}</div>
    <div className="pft-stat__value">{value}</div>
    {sub ? <div className="pft-stat__sub">{sub}</div> : null}
  </div>
);

/**
 * The Renewals report: the Mastersheet's Renewals snapshot (ending · renewed · re-rented ·
 * pending · open, per building with a total) for a chosen window — to the end of next
 * month, or the next 30 / 60 / 90 days — over every property in the viewer's scope, or one
 * of them. `rows` are unit rows of the tiles' shape (unitStats.js); the server already
 * scopes them to the viewer's properties, so a manager only ever sees their own.
 */
const RenewalsTab = ({ rows }) => {
  const [windowId, setWindowId] = useState("90");
  const [view, setView] = useState("ending");
  const [property, setProperty] = useState("");

  const properties = useMemo(() => [...new Set(rows.map((r) => r.property_name).filter(Boolean))].sort(), [rows]);
  const scoped = useMemo(() => (property ? rows.filter((r) => r.property_name === property) : rows), [rows, property]);
  const win = useMemo(() => (WINDOWS.find((w) => w.id === windowId) || WINDOWS[3]).make(), [windowId]);
  const stats = useMemo(() => summarizeUnits(scoped, undefined, win), [scoped, win]);
  const detail = useMemo(() => availabilityDetail(scoped, undefined, win), [scoped, win]);

  if (!stats.hasRentedInfo) {
    return <Alert color="warning" className="mb-0">Lease information is not available for this sheet yet.</Alert>;
  }

  return (
    <div data-testid="renewals-report">
      <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
        <div>
          <div className="pft-stat__label mb-1">Window</div>
          <select className="form-select form-select-sm" value={windowId} onChange={(e) => setWindowId(e.target.value)} aria-label="Window">
            {WINDOWS.map((w) => <option key={w.id} value={w.id}>{w.label(w.make())}</option>)}
          </select>
        </div>
        {properties.length > 1 && (
          <div>
            <div className="pft-stat__label mb-1">Property</div>
            <select className="form-select form-select-sm" value={property} onChange={(e) => setProperty(e.target.value)} aria-label="Property">
              <option value="">All properties ({properties.length})</option>
              {properties.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}
        <div className="btn-group btn-group-sm" role="group" aria-label="View">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`btn ${view === v.id ? "btn-primary" : "btn-outline-primary"}`}
              aria-pressed={view === v.id}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pft-stats" data-testid="renewals-kpis">
        <Kpi label={`Ending ${win.startLabel} – ${win.endLabel}`} value={stats.expiring} sub={`${win.daysToEnd} days`} tone="pft-stat--renewals" />
        <Kpi label="Renewed" value={stats.renewedExpiring} sub="same resident, signed" tone="pft-stat--occupied" />
        <Kpi label="Re-rented" value={stats.rentedExpiring - stats.renewedExpiring} sub="new resident, signed" tone="pft-stat--rented" />
        <Kpi label="Pending" value={stats.upcomingExpiring} sub="awaiting signatures" />
        <Kpi
          label="Still open"
          value={<span className="pft-open">{stats.openExpiring}</span>}
          sub={`${stats.expiring} − ${stats.rentedExpiring} renewed or rented`}
        />
        <Kpi label="Vacant now" value={stats.vacant} sub={`${stats.rentedVacant} already rented`} />
      </div>

      <AvailabilityPopover inline kind={view} detail={detail} stats={stats} hasRentedInfo={stats.hasRentedInfo} />
    </div>
  );
};

export default RenewalsTab;
